import type { Editor } from '@tiptap/core'
import type { TiptapCollabProvider } from '@tiptap-pro/provider'
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useStore } from 'zustand'
import type { DocumentId } from '../CollaborationTextEditor/types'
import { getUnresolvedThreads } from '../CollaborationTextEditor/utils'
import {
  createTiptapEditorRegistryStore,
  type TiptapEditorRegistry,
  type TiptapEditorRegistryStore,
  type TiptapEditorResource,
  type RegisteredTiptapEditor,
} from './store'

const TiptapEditorRegistryContext = createContext<
  TiptapEditorRegistryStore | undefined
>(undefined)

/**
 * Provides the registry of mounted TipTap editors. Owned by the Gem copilot
 * transport (`CopilotTransportProvider`), which renders this provider around
 * the editors it reads. Standalone editors rendered outside the transport
 * (e.g. tests, the Post embed) mount it directly.
 */
export const TiptapEditorRegistryProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [store] = useState(() => createTiptapEditorRegistryStore())

  return (
    <TiptapEditorRegistryContext.Provider value={store}>
      {children}
    </TiptapEditorRegistryContext.Provider>
  )
}

/**
 * Subscribe to the editor registry state with a selector.
 */
export const useTiptapEditorRegistry = <T,>(
  selector: (state: TiptapEditorRegistry) => T
) => {
  const store = useContext(TiptapEditorRegistryContext)
  if (!store) {
    throw new Error(
      'useTiptapEditorRegistry must be used within a <TiptapEditorRegistryProvider>'
    )
  }

  return useStore(store, selector)
}

/**
 * Register a Tiptap editor with the registry for its lifetime. Called by
 * `CollaborationTextEditor` once the editor instance is available.
 */
export const useRegisterTiptapEditor = (
  documentId: DocumentId | undefined,
  editor: Editor | null,
  resource: TiptapEditorResource | null | undefined,
  provider: TiptapCollabProvider | null | undefined
) => {
  const store = useContext(TiptapEditorRegistryContext)
  const resourceType = resource?.type
  const resourceId = resource?.id
  const resourceName = resource?.name

  useEffect(() => {
    if (!documentId || !editor || !store) return

    const focusEditor = () => store.getState().focusEditor(documentId)
    if (editor.isFocused) focusEditor()
    if (typeof editor.on !== 'function') return

    editor.on('focus', focusEditor)

    return () => {
      editor.off?.('focus', focusEditor)
    }
  }, [documentId, editor, store])

  useEffect(() => {
    if (!documentId || !editor) return

    if (!store) {
      console.warn('No editor registry context is available to register with.')
      return
    }

    const resolvedResource =
      resourceType && resourceId
        ? { type: resourceType, id: resourceId, name: resourceName }
        : null
    const entry = {
      documentId,
      editor,
      resource: resolvedResource,
      readThreads: () => getUnresolvedThreads(editor, provider ?? null),
      isLastFocused: () =>
        store.getState().lastFocusedDocumentId === documentId,
    }
    store.getState().registerEditor(entry)

    // Editability is mutable state on the editor instance. The view-mode
    // selector calls `editor.setEditable` directly, so nothing about it changes
    // by reference. Consumers that care whether a document can be written to
    // (the Gem copilot, which resumes queued work only into an editable
    // document) must learn when the user switches out of view-only.
    //
    // `setEditable` dispatches a transaction, so that event is the signal. It
    // also fires per keystroke, so the entry is published again only when
    // editability actually changes. A fresh entry replaces the old one under
    // the same documentId: consumers re-derive from identity. Subscribed here
    // rather than via `useEditorState` so this stays usable with the stand-in
    // editors tests mount.
    let isEditable = editor.isEditable
    const syncEditable = () => {
      if (editor.isEditable === isEditable) return

      isEditable = editor.isEditable
      store.getState().registerEditor({ ...entry })
    }
    if (typeof editor.on === 'function') {
      editor.on('transaction', syncEditable)
    }

    return () => {
      editor.off?.('transaction', syncEditable)
      store.getState().unregisterEditor(documentId)
    }
  }, [
    documentId,
    editor,
    provider,
    resourceId,
    resourceName,
    resourceType,
    store,
  ])
}

/**
 * Returns currently mounted editors. A view may expose one or several
 * at once (e.g. one per board collection), so the Gem sends all and
 * targets edits by `documentId`.
 */
export const useRegisteredTiptapEditors = (): RegisteredTiptapEditor[] => {
  const editors = useTiptapEditorRegistry((state) => state.editors)
  return useMemo(() => Array.from(editors.values()), [editors])
}
